import { Link } from "react-router-dom";
import type { Article } from "@/types/content";
import InfoBadge from "@/components/base/InfoBadge";
import { formatDate, getCategoryName } from "@/utils/content";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "compact" | "featured";
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex cursor-pointer items-start gap-3 border-b border-background-200 py-3.5 last:border-0"
      >
        <span className="mt-0.5 font-heading text-lg font-bold text-background-400 transition-colors group-hover:text-primary-500">
          <i className="ri-article-line" aria-hidden="true"></i>
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-700">
            {article.title}
          </span>
          <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <span>{getCategoryName(article.category)}</span>
            <span aria-hidden="true">·</span>
            <span>{formatDate(article.date)}</span>
          </span>
        </span>
      </Link>
    );
  }

  if (variant === "featured") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group grid cursor-pointer overflow-hidden rounded-lg border border-background-200 bg-background-50 md:grid-cols-2"
      >
        <div className="relative h-56 w-full overflow-hidden md:h-full md:min-h-[320px]">
          <img
            src={article.cover}
            alt={`${article.title} - 灵灵事件信息梳理配图`}
            title={`${article.title}｜灵灵事件梳理`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute left-3 top-3">
            <InfoBadge level={article.infoLevel} />
          </span>
        </div>
        <div className="flex flex-col justify-center p-5 md:p-8">
          <span className="flex items-center gap-2 text-xs font-medium tracking-widest text-primary-600">
            <span className="h-px w-5 bg-primary-500" aria-hidden="true"></span>
            {getCategoryName(article.category)}
          </span>
          <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-950 group-hover:text-primary-700 md:text-2xl">
            {article.title}
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 line-clamp-3">{article.excerpt}</p>
          <span className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
            <span>{article.author}</span>
            <span>{formatDate(article.date)}</span>
            <span>阅读约 {article.readTime}</span>
          </span>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="relative h-44 w-full overflow-hidden sm:h-48">
        <img
          src={article.cover}
          alt={`${article.title} - 灵灵事件信息梳理配图`}
          title={`${article.title}｜灵灵事件梳理`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3">
          <InfoBadge level={article.infoLevel} />
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <span className="text-xs font-medium tracking-widest text-primary-600">
          {getCategoryName(article.category)}
        </span>
        <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-700 md:text-lg">
          {article.title}
        </h3>
        <p className="mt-2 flex-1 text-xs leading-relaxed text-foreground-600 line-clamp-3 md:text-sm">
          {article.excerpt}
        </p>
        <span className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-background-100 pt-3 text-xs text-foreground-500">
          <span>{formatDate(article.date)}</span>
          <span aria-hidden="true">·</span>
          <span>阅读约 {article.readTime}</span>
        </span>
      </div>
    </Link>
  );
}